import { useMemo, type FC } from "react";
import useDarkMode from "@/hooks/useDarkMode";
import { buildSizeBubbleOption } from "@/pages/CampaignOverviewPage/charts/bubbleChartOptions";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/TopPerformers.module.css";

interface SizeBubblePanelProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

const PANEL_TEXT = TEXT.campaignOverview.topPerformers.sizeBubble;
const CREATIVE_TEXT = TEXT.campaignOverview.creativePerformance;

const CHART_STYLE = { height: "100%", width: "100%" } as const;

// Detail panel for the "Top creative size by CTR" tile: each size plotted by
// volume (log x) and CTR (y), bubble area proportional to clicks.
const SizeBubblePanel: FC<SizeBubblePanelProps> = ({
  error,
  isLoading,
  rows,
}) => {
  const isDark = useDarkMode();
  const option = useMemo(
    () => buildSizeBubbleOption(rows, isDark),
    [rows, isDark]
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>{PANEL_TEXT.title}</span>
          <span className={styles.subtitle}>{PANEL_TEXT.subtitle}</span>
        </div>
      </div>
      <div className={styles.chartFrame}>
        <ChartStateBody
          error={error}
          errorMessage={CREATIVE_TEXT.error}
          isEmpty={rows.length === 0}
          isLoading={isLoading}
          loadingClassName={styles.loading}
          messageClassName={styles.message}
          noDataMessage={CREATIVE_TEXT.emptyMessage}
          option={option}
          style={CHART_STYLE}
        />
      </div>
      <div className={styles.key}>
        <span className={styles.keyItem}>
          <span className={styles.keyDot} />
          {PANEL_TEXT.oneSizeKey}
        </span>
        <span className={styles.keyItem}>{PANEL_TEXT.bubbleSizeKey}</span>
      </div>
    </div>
  );
};

export default SizeBubblePanel;
